export interface SvgProcessOptions {
  fillMode: 'original' | 'monochrome' | 'custom';
  monochromeColor?: string;
  colorMapping?: Record<string, string>;
}

/**
 * Processes SVG content by applying color overrides based on fill mode.
 * 
 * @param svgText The raw SVG content string
 * @param options Options for processing (fillMode, colors)
 * @returns The processed SVG content string
 */
export const processSvg = (svgText: string, options: SvgProcessOptions): string => {
  if (typeof DOMParser === 'undefined') {
    // Fallback for non-browser environments (if any)
    return svgText;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'image/svg+xml');
  const svgElement = doc.documentElement;

  // Check for parser errors
  const parserError = svgElement.querySelector('parsererror');
  if (parserError) {
    console.warn('Failed to parse SVG:', parserError.textContent);
    return svgText;
  }

  const { fillMode, monochromeColor, colorMapping } = options;

  // Enforce preserveAspectRatio="none" to allow stretching/squashing
  svgElement.setAttribute('preserveAspectRatio', 'none');
  // Ensure width and height are 100% to fill the container if not set, 
  // or let the container control the size via CSS.
  // Actually, setting width/height to 100% is often good for scaling.
  if (!svgElement.hasAttribute('width')) svgElement.setAttribute('width', '100%');
  if (!svgElement.hasAttribute('height')) svgElement.setAttribute('height', '100%');

  if (fillMode === 'monochrome' && monochromeColor) {
    const elements = svgElement.querySelectorAll('*');
    elements.forEach(el => {
      // Handle fill
      const fill = el.getAttribute('fill');
      if (fill && fill !== 'none' && fill !== 'currentColor' && !fill.startsWith('url(')) {
        el.setAttribute('fill', monochromeColor);
      }
      
      // Handle stroke
      const stroke = el.getAttribute('stroke');
      if (stroke && stroke !== 'none' && stroke !== 'currentColor' && !stroke.startsWith('url(')) {
        el.setAttribute('stroke', monochromeColor);
      }
      
      // Handle style attribute
      if (el instanceof SVGElement || el instanceof HTMLElement) {
          const style = el.getAttribute('style');
          if (style) {
            // Check if style contains fill or stroke
            if (style.includes('fill:') || style.includes('stroke:')) {
               // We can't easily parse style string without a library or regex.
               // Simple regex replacement for common cases
               let newStyle = style;
               // Replace fill: ... ;
               newStyle = newStyle.replace(/fill:\s*([^;]+)/gi, (match, val) => {
                 if (val === 'none' || val === 'currentColor' || val.startsWith('url(')) return match;
                 return `fill: ${monochromeColor}`;
               });
               // Replace stroke: ... ;
               newStyle = newStyle.replace(/stroke:\s*([^;]+)/gi, (match, val) => {
                 if (val === 'none' || val === 'currentColor' || val.startsWith('url(')) return match;
                 return `stroke: ${monochromeColor}`;
               });
               el.setAttribute('style', newStyle);
            }
          }
      }
    });
  } else if ((fillMode === 'custom' || fillMode === 'original') && colorMapping) {
      if (Object.keys(colorMapping).length > 0) {
          const elements = svgElement.querySelectorAll('*');
          elements.forEach(el => {
              // Check fill
              const fill = el.getAttribute('fill');
              if (fill && colorMapping[fill.toLowerCase()]) {
                  el.setAttribute('fill', colorMapping[fill.toLowerCase()]);
              }
              // Check stroke
              const stroke = el.getAttribute('stroke');
              if (stroke && colorMapping[stroke.toLowerCase()]) {
                  el.setAttribute('stroke', colorMapping[stroke.toLowerCase()]);
              }
              
              // Check style
              const style = el.getAttribute('style');
              if (style) {
                  let newStyle = style;
                  Object.entries(colorMapping).forEach(([orig, newC]) => {
                      // Escape regex special characters in original color
                      const escapedOrig = orig.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                      // Regex to replace color (case insensitive)
                      newStyle = newStyle.replace(new RegExp(escapedOrig, 'gi'), newC);
                  });
                  el.setAttribute('style', newStyle);
              }
          });
      }
  }

  const serializer = new XMLSerializer();
  return serializer.serializeToString(svgElement);
};
